/**
 * CashflowTab - Inflow/outflow monthly bars + balance trend
 */

import React from 'react';
import type { CashflowTabProps } from '../../../../types/lending-report.types';
import { formatINR } from '../../../../types/lending-report.types';
import { getMonthLabel, formatShort } from '../../../../utils/lending-report-utils';

const BALANCE_THRESHOLD = 10000;

const DualBarChart: React.FC<{
  inflow: { month?: string; amount?: number; averageAmount?: number }[];
  outflow: { month?: string; amount?: number; averageAmount?: number }[];
}> = ({ inflow, outflow }) => {
  const months = Math.max(inflow.length, outflow.length);
  if (months === 0) {
    return <div className="text-gray-400 text-sm text-center py-8">No cashflow data available</div>;
  }

  const getAmount = (entry: { amount?: number; averageAmount?: number } | undefined) =>
    entry?.amount || entry?.averageAmount || 0;

  const allAmounts = [
    ...inflow.map(getAmount),
    ...outflow.map(getAmount),
  ];
  const maxVal = Math.max(...allAmounts, 1);

  // Generate Y-axis ticks (5 ticks from 0 to max)
  const tickCount = 5;
  const niceMax = Math.ceil(maxVal / Math.pow(10, Math.floor(Math.log10(maxVal)))) * Math.pow(10, Math.floor(Math.log10(maxVal)));
  const tickStep = niceMax / tickCount;
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) => i * tickStep);


  const width = 600;
  const height = 220;
  const padding = { top: 10, right: 20, bottom: 40, left: 55 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const barGroupWidth = chartW / months;
  const barWidth = barGroupWidth * 0.3;
  const barGap = 2;

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 240 }}>
        {/* Y-axis grid lines and labels */}
        {yTicks.map((tick, i) => {
          const y = padding.top + chartH - (tick / niceMax) * chartH;
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e5e7eb" strokeWidth={0.5} />
              <text x={padding.left - 8} y={y + 3} textAnchor="end" fill="#9ca3af" fontSize={10}>
                {formatShort(tick)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {Array.from({ length: months }).map((_, i) => {
          const inflowVal = getAmount(inflow[i]);
          const outflowVal = getAmount(outflow[i]);
          const inflowH = (inflowVal / niceMax) * chartH;
          const outflowH = (outflowVal / niceMax) * chartH;
          const groupX = padding.left + i * barGroupWidth + barGroupWidth / 2;
          const label = getMonthLabel(inflow[i]?.month || outflow[i]?.month) || `${i + 1}`;

          return (
            <g key={i}>
              {/* Inflow bar (blue) */}
              <rect
                x={groupX - barWidth - barGap / 2}
                y={padding.top + chartH - inflowH}
                width={barWidth}
                height={Math.max(inflowH, 1)}
                fill="#6366f1"
                rx={2}
              >
                <title>Inflow: {formatINR(inflowVal)}</title>
              </rect>
              {/* Outflow bar (gray) */}
              <rect
                x={groupX + barGap / 2}
                y={padding.top + chartH - outflowH}
                width={barWidth}
                height={Math.max(outflowH, 1)}
                fill="#d1d5db"
                rx={2}
              >
                <title>Outflow: {formatINR(outflowVal)}</title>
              </rect>
              {/* X-axis label */}
              <text
                x={groupX}
                y={height - padding.bottom + 18}
                textAnchor="middle"
                fill="#6b7280"
                fontSize={10}
              >
                {label}
              </text>
            </g>
          );
        })}

        {/* X-axis baseline */}
        <line
          x1={padding.left}
          y1={padding.top + chartH}
          x2={width - padding.right}
          y2={padding.top + chartH}
          stroke="#d1d5db"
          strokeWidth={1}
        />
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginTop: '8px', fontSize: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#6366f1' }} />
          <span style={{ color: '#374151' }}>Inflow</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#d1d5db' }} />
          <span style={{ color: '#374151' }}>Outflow</span>
        </div>
      </div>
    </div>
  );
};

const BalanceTrendLine: React.FC<{ data: { month?: string; amount?: number; averageAmount?: number }[] }> = ({ data }) => {
  if (data.length === 0) {
    return <div className="text-gray-400 text-sm text-center py-8">No balance data available</div>;
  }

  const width = 600;
  const height = 220;
  const padding = { top: 15, right: 20, bottom: 40, left: 55 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const amounts = data.map(d => d.amount || d.averageAmount || 0);
  const maxVal = Math.max(...amounts, 1);
  const minVal = Math.min(...amounts, 0);

  // Nice Y-axis range
  const range = maxVal - Math.min(minVal, 0);
  const niceMax = Math.ceil(maxVal / Math.pow(10, Math.floor(Math.log10(range || 1)))) * Math.pow(10, Math.floor(Math.log10(range || 1)));
  const niceMin = 0;

  const tickCount = 5;
  const tickStep = (niceMax - niceMin) / tickCount;
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) => niceMin + i * tickStep);


  const getY = (val: number) => padding.top + chartH - ((val - niceMin) / (niceMax - niceMin)) * chartH;
  const getX = (i: number) => padding.left + (i / Math.max(data.length - 1, 1)) * chartW;

  const points = data.map((d, i) => {
    const val = d.amount || d.averageAmount || 0;
    return { x: getX(i), y: getY(val), val, month: d.month, belowThreshold: val < BALANCE_THRESHOLD };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Threshold line Y position
  const thresholdY = getY(BALANCE_THRESHOLD);
  const showThreshold = BALANCE_THRESHOLD <= niceMax;

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 240 }}>
        {/* Y-axis grid lines and labels */}
        {yTicks.map((tick, i) => {
          const y = getY(tick);
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e5e7eb" strokeWidth={0.5} />
              <text x={padding.left - 8} y={y + 3} textAnchor="end" fill="#9ca3af" fontSize={10}>
                {formatShort(tick)}
              </text>
            </g>
          );
        })}

        {/* Threshold line */}
        {showThreshold && (
          <line
            x1={padding.left}
            y1={thresholdY}
            x2={width - padding.right}
            y2={thresholdY}
            stroke="#ef4444"
            strokeWidth={1}
            strokeDasharray="4,3"
            opacity={0.5}
          />
        )}

        {/* Line path */}
        <path d={pathD} fill="none" stroke="#6366f1" strokeWidth={2} />

        {/* Data points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r={4}
              fill={p.belowThreshold ? '#ef4444' : '#6366f1'}
              stroke="white"
              strokeWidth={1.5}
            >
              <title>{getMonthLabel(p.month)}: {formatINR(p.val)}</title>
            </circle>
          </g>
        ))}

        {/* X-axis labels */}
        {points.map((p, i) => (
          <text key={i} x={p.x} y={height - padding.bottom + 18} textAnchor="middle" fill="#6b7280" fontSize={10}>
            {getMonthLabel(p.month) || `${i + 1}`}
          </text>
        ))}

        {/* X-axis baseline */}
        <line
          x1={padding.left}
          y1={padding.top + chartH}
          x2={width - padding.right}
          y2={padding.top + chartH}
          stroke="#d1d5db"
          strokeWidth={1}
        />
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginTop: '8px', fontSize: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
          <span style={{ color: '#374151' }}>Below &#8377;10,000</span>
        </div>
      </div>
    </div>
  );
};

export const CashflowTab: React.FC<CashflowTabProps> = ({ report }) => {
  return (
    <div className="space-y-6">
      {/* Inflow vs Outflow */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Inflow vs Outflow</h3>
        <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
          <DualBarChart inflow={report.monthlyInflowHistory} outflow={report.monthlyOutflowHistory} />
        </div>
      </div>

      {/* Balance Trend */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">End-of-Month Balance Trend</h3>
        <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
          <BalanceTrendLine data={report.monthlyBalanceHistory} />
        </div>
      </div>
    </div>
  );
};
